// Portfolio scope bar — Account → Basket → Strategy drill, shared across the
// Performance and Risk tabs. Self-contained: loads accounts/baskets/strategies,
// owns the level + selections, syncs the shared account (single-select), and emits
// the resolved scope via onChange({accountIds, portfolioIds, strategyIds, level}).
// Same math the backend _resolve_perf_scope expects (a component-set filter).
import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import useSharedAccount from './useSharedAccount';
import { MultiPicker, GroupedPicker } from './pickers';

const ScopeBar = ({ onChange }) => {
  const [accounts, setAccounts] = useState([]);
  const [sharedAccountId, setSharedAccountId] = useSharedAccount();
  const [selectedIds, setSelectedIds] = useState(null); // null until accounts load; [] = all
  const [level, setLevel] = useState('portfolio'); // 'portfolio' | 'basket' | 'strategy'
  const [portfolioIds, setPortfolioIds] = useState([]);
  const [strategyIds, setStrategyIds] = useState([]);
  const [baskets, setBaskets] = useState([]);
  const [allStrategies, setAllStrategies] = useState([]);

  useEffect(() => {
    tradingService.listIBKRAccounts()
      .then((r) => {
        setAccounts(r.data?.results || r.data || []);
        setSelectedIds(sharedAccountId != null ? [sharedAccountId] : []);
      })
      .catch(() => setAccounts([]));
    tradingService.listPortfolios().then((r) => setBaskets(r.data?.results || r.data || [])).catch(() => {});
    tradingService.getStrategies().then((r) => setAllStrategies(r.data?.results || r.data || [])).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Follow the shared account when it changes elsewhere (only while single-select).
  useEffect(() => {
    if (selectedIds && selectedIds.length === 1 && sharedAccountId != null && selectedIds[0] !== sharedAccountId) {
      setSelectedIds([sharedAccountId]);
    }
  }, [sharedAccountId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const onScopeChange = (ids) => {
    setSelectedIds(ids);
    if (ids.length === 1) setSharedAccountId(ids[0]);
  };

  const scopedStrategies = useMemo(() => {
    const inScope = (s) => !selectedIds || selectedIds.length === 0 || selectedIds.includes(s.account);
    return allStrategies.filter(inScope).map((s) => ({ id: s.id, name: s.name, tag: s.account_is_paper ? 'paper' : 'live' }));
  }, [allStrategies, selectedIds]);

  const strategyGroups = useMemo(() => {
    const inScope = new Set(scopedStrategies.map((s) => s.id));
    const groups = [];
    const grouped = new Set();
    baskets.forEach((b) => {
      const kids = (b.members || []).filter((m) => inScope.has(m.strategy_id)).map((m) => ({ id: m.strategy_id, name: m.strategy_name }));
      if (kids.length) { groups.push({ key: `b${b.id}`, label: b.name, children: kids }); kids.forEach((k) => grouped.add(k.id)); }
    });
    const ungrouped = scopedStrategies.filter((s) => !grouped.has(s.id)).map((s) => ({ id: s.id, name: s.name }));
    if (ungrouped.length) groups.push({ key: 'ungrouped', label: 'Ungrouped', children: ungrouped });
    return groups;
  }, [baskets, scopedStrategies]);

  // Emit the resolved scope whenever it changes.
  useEffect(() => {
    if (selectedIds == null) return;
    onChange({
      accountIds: selectedIds.length ? selectedIds : undefined,
      portfolioIds: level === 'basket' && portfolioIds.length ? portfolioIds : undefined,
      strategyIds: level === 'strategy' && strategyIds.length ? strategyIds : undefined,
      level,
    });
  }, [selectedIds, level, portfolioIds, strategyIds]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2">
      {accounts.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 w-16">Accounts</span>
          <MultiPicker items={accounts.map((a) => ({ id: a.id, name: a.name, tag: a.is_paper ? 'paper' : 'live' }))}
            selected={selectedIds} onChange={onScopeChange} allLabel="All accounts (consolidated)" singular="account" />
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-gray-400 w-16">Level</span>
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm">
          {[['portfolio', 'Portfolio'], ['basket', 'Basket'], ['strategy', 'Strategy']].map(([lv, lab]) => (
            <button key={lv} onClick={() => setLevel(lv)}
              className={`px-3 py-1.5 ${level === lv ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>{lab}</button>
          ))}
        </div>
        {level === 'basket' && (
          <MultiPicker
            items={baskets.map((b) => ({ id: b.id, name: b.name, tag: b.is_active ? 'active' : 'inactive' }))}
            selected={portfolioIds} onChange={setPortfolioIds}
            allLabel="Pick baskets to combine…" singular="basket" width="min-w-[220px]" />
        )}
        {level === 'strategy' && (
          <GroupedPicker groups={strategyGroups} selected={strategyIds} onChange={setStrategyIds}
            allLabel="All strategies in scope" singular="strategy" />
        )}
        {level === 'basket' && portfolioIds.length === 0 && <span className="text-[11px] text-gray-400">tick one or more baskets to combine</span>}
        {level === 'strategy' && strategyIds.length === 0 && <span className="text-[11px] text-gray-400">showing all strategies in scope</span>}
      </div>
    </div>
  );
};

export default ScopeBar;
